import { defineComponent, inject, ref, computed, watchEffect, watch, onMounted, nextTick, resolveComponent, openBlock, createElementBlock, Fragment, createBlock, resolveDynamicComponent, withCtx, createTextVNode, toDisplayString, createCommentVNode, createElementVNode, normalizeClass, unref, renderList, createVNode } from 'vue';
import { resolveLinkKey, imageResize } from '@distancify/drapejs-storyblok';
import MarkdownIt from 'markdown-it';

const sizeRegex = /(?<width>\d+)x(?<height>\d+)/;
function styleBuild (block) {
  const rules = [[buildStyleRule(`.block-image--${block._uid} .block-image__image, .block-image--${block._uid} .block-image__video`, getImageStyle(block, '')), buildStyleRule(`.block-image--${block._uid} .block-image__margin`, getMarginStyle(block, '')), buildStyleRule(`.block-image--${block._uid} .block-image__frame`, getContainerStyle(block, '')), buildStyleRule(`.block-image--${block._uid} .block-image__frame-spacer`, getSpacerStyle(block, ''))].join(' '), buildStyleRule(`@media (min-width: ${block.breakpointMedium}px)`, [buildStyleRule(`.block-image--${block._uid} .block-image__image, .block-image--${block._uid} .block-image__video`, getImageStyle(block, 'Medium')), buildStyleRule(`.block-image--${block._uid} .block-image__margin`, getMarginStyle(block, 'Medium')), buildStyleRule(`.block-image--${block._uid} .block-image__frame`, getContainerStyle(block, 'Medium')), buildStyleRule(`.block-image--${block._uid} .block-image__frame-spacer`, getSpacerStyle(block, 'Medium'))].join(' ')), buildStyleRule(`@media (min-width: ${block.breakpointLarge}px)`, [buildStyleRule(`.block-image--${block._uid} .block-image__image, .block-image--${block._uid} .block-image__video`, getImageStyle(block, 'Large')), buildStyleRule(`.block-image--${block._uid} .block-image__margin`, getMarginStyle(block, 'Large')), buildStyleRule(`.block-image--${block._uid} .block-image__frame`, getContainerStyle(block, 'Large')), buildStyleRule(`.block-image--${block._uid} .block-image__frame-spacer`, getSpacerStyle(block, 'Large'))].join(' '))];

  if (block.overlays) {
    for (let o of block.overlays) {
      rules.push(buildStyleRule(`.block-image__overlay-container--${o._uid}`, getOverlayContainerStyle(o, 'Small')));
      rules.push(buildStyleRule(`.block-image__overlay--${o._uid}`, getOverlayStyle(o, 'Small')));
      rules.push(buildStyleRule(`@media (min-width: ${block.breakpointMedium}px)`, buildStyleRule(`.block-image__overlay-container--${o._uid}`, getOverlayContainerStyle(o, 'Medium'))));
      rules.push(buildStyleRule(`@media (min-width: ${block.breakpointMedium}px)`, buildStyleRule(`.block-image__overlay--${o._uid}`, getOverlayStyle(o, 'Medium'))));
      rules.push(buildStyleRule(`@media (min-width: ${block.breakpointLarge}px)`, buildStyleRule(`.block-image__overlay-container--${o._uid}`, getOverlayContainerStyle(o, 'Large'))));
      rules.push(buildStyleRule(`@media (min-width: ${block.breakpointLarge}px)`, buildStyleRule(`.block-image__overlay--${o._uid}`, getOverlayStyle(o, 'Large'))));
    }
  }

  return rules.join('');
}

function buildStyleRule(selector, properties) {
  if (!properties) {
    return '';
  }

  if (typeof properties !== 'string') {
    return `${selector}{${properties.join(' ')}}`;
  }

  return `${selector}{${properties}}`;
}

function getImageStyle(block, device) {
  var _block$image, _block$image2;

  const height = block[`height${device}`];
  const result = [];

  if ((height === null || height === void 0 ? void 0 : height.type) !== 'unset') {
    result.push('height: auto; width: 100%;');

    if ((height === null || height === void 0 ? void 0 : height.type) === 'css' || (height === null || height === void 0 ? void 0 : height.type) === 'viewport') {
      result.push('height: 100%;');
    } else if ((height === null || height === void 0 ? void 0 : height.type) === "aspectRatio") {
      result.push('height: 100%; left: 0; right: 0; top: 0; bottom: 0; position: absolute;');
    } else {
      result.push('left: auto; right: auto; top: auto; bottom: auto; position: static;');
    }
  }

  if ((_block$image = block.image) !== null && _block$image !== void 0 && _block$image.focus && (_block$image2 = block.image) !== null && _block$image2 !== void 0 && _block$image2.filename) {
    var _sizeRegex$exec;

    const [focalX, focalY] = block.image.focus.split(':')[0].split('x');
    const groups = (_sizeRegex$exec = sizeRegex.exec(block.image.filename)) === null || _sizeRegex$exec === void 0 ? void 0 : _sizeRegex$exec.groups;

    if (groups) {
      result.push(`object-position:${parseFloat(focalX) / parseInt(groups['width']) * 100}% ${parseFloat(focalY) / parseInt(groups['height']) * 100}%;`);
    }
  }

  if (block[`fit${device}`]) {
    result.push(getStyleValue(block, 'object-fit', 'fit', device));
  }

  if (block[`borderRadius${device}`]) {
    result.push(getStyleValue(block, 'border-radius', 'borderRadius', device));
  }

  return result.join('');
}

function getStyleValue(block, property, field, device) {
  if (!block[field + device]) {
    return null;
  }

  return `${property}: ${block[field + device]};`;
}

function getWidthStyleValue(property, field, obj) {
  if (!obj[field]) {
    return null;
  }

  return `${property}: ${obj[field]};`;
}

function getHeightStyleValue(type, obj) {
  if (type === 'css') {
    return `height: ${obj[type]};`;
  }

  if (type === 'viewport') {
    return `height: calc(${obj[type]} * var(--storyblok-image-vh));`;
  }

  return `height: auto;`;
}

function getMarginStyle(block, device) {
  var _block$;

  const width = block[`width${device}`];
  const alignment = (_block$ = block[`alignment${device}`]) === null || _block$ === void 0 ? void 0 : _block$.split(',');
  const result = [];

  if (width !== null && width !== void 0 && width.setWidth) {
    result.push(getWidthStyleValue('width', 'useWidth', width));
  }

  if (width !== null && width !== void 0 && width.setMaxWidth) {
    result.push(getWidthStyleValue('max-width', 'useMaxWidth', width));
  }

  if ((alignment === null || alignment === void 0 ? void 0 : alignment.length) === 2) {
    result.push(`margin-left: ${alignment[0]}; margin-right: ${alignment[1]}`);
  }

  return result.join('');
}

function getContainerStyle(block, device) {
  var _block$2;

  const height = block[`height${device}`];
  const width = block[`width${device}`];
  const alignment = (_block$2 = block[`alignment${device}`]) === null || _block$2 === void 0 ? void 0 : _block$2.split(',');
  const result = [];

  if (height) {
    result.push(getHeightStyleValue(height.type, height));
  }

  if (width !== null && width !== void 0 && width.setWidth) {
    result.push(getWidthStyleValue('width', 'useWidth', width));
  }

  if (width !== null && width !== void 0 && width.setMaxWidth) {
    result.push(getWidthStyleValue('max-width', 'useMaxWidth', width));
  }

  if ((alignment === null || alignment === void 0 ? void 0 : alignment.length) === 2) {
    result.push(`margin-left: ${alignment[0]}; margin-right: ${alignment[1]}`);
  }

  return result.join('');
}

function getSpacerStyle(block, device) {
  const height = block[`height${device}`];
  const aspectRatioForDevice = (height === null || height === void 0 ? void 0 : height.type) === "aspectRatio";
  const result = [];

  if ((height === null || height === void 0 ? void 0 : height.type) !== 'unset') {
    if (aspectRatioForDevice) {
      result.push(`padding-top:${100 * height.aspectRatio}%; display:block`);
    } else {
      result.push('display:none');
    }
  }

  return result.join('');
}

function getOverlayContainerStyle(overlay, device) {
  var _overlay$show;

  if (overlay.show && !((_overlay$show = overlay.show) !== null && _overlay$show !== void 0 && _overlay$show.includes(device))) {
    return 'display: none';
  }

  const pos = overlay[`anchorPoint${device}`];
  return `display:block;transform:translate(${pos.x}%,${pos.y}%);`;
}

function getOverlayStyle(overlay, device) {
  var _overlay$show2;

  if (overlay.show && !((_overlay$show2 = overlay.show) !== null && _overlay$show2 !== void 0 && _overlay$show2.includes(device))) {
    return '';
  }

  const result = [];
  const pos = overlay[`anchorPoint${device}`];
  result.push(`transform:translate(-${pos.x}%,-${pos.y}%);`);
  const maxWidth = overlay[`maxWidth${device}`];

  if (maxWidth) {
    result.push(`max-width:${maxWidth};`);
  }

  return result.join('');
}

const _hoisted_1 = {
  class: "block-image__frame"
};

const _hoisted_2 = /*#__PURE__*/createElementVNode("div", {
  class: "block-image__frame-spacer"
}, null, -1);

const _hoisted_3 = ["src", "width", "height", "alt"];
const _hoisted_4 = {
  key: 1,
  class: "block-image__video",
  autoplay: "",
  playsinline: "",
  muted: "",
  loop: ""
};
const _hoisted_5 = ["src"];
const _hoisted_6 = ["innerHTML"];
const _hoisted_7 = ["innerHTML"];
var script = /*#__PURE__*/defineComponent({
  name: 'storyblok-image',
  props: {
    block: null
  },

  setup(__props) {
    const props = __props;

    if (typeof window !== 'undefined') {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--storyblok-image-vh', `${vh}px`);
    }

    const resolveLink = inject(resolveLinkKey);
    const loaded = ref(false);
    const style = ref('');
    const lowres = imageResize(props.block.image, {
      width: 10
    });
    const displayedUrl = ref(lowres.filename);
    const imgElement = ref();
    const hiresImage = ref();
    const isVideo = computed(() => {
      var _props$block$image, _props$block$image$fi;

      return (((_props$block$image = props.block.image) === null || _props$block$image === void 0 ? void 0 : (_props$block$image$fi = _props$block$image.filename) === null || _props$block$image$fi === void 0 ? void 0 : _props$block$image$fi.indexOf('.mp4')) || 0) > 0;
    });
    const containerClasses = computed(() => {
      const result = ['block-image', `block-image--${props.block._uid}`];
      return result;
    });
    const alt = computed(() => {
      var _props$block$image2;

      return (_props$block$image2 = props.block.image) === null || _props$block$image2 === void 0 ? void 0 : _props$block$image2.alt;
    });
    const caption = computed(() => {
      var _props$block$image3;

      return (_props$block$image3 = props.block.image) !== null && _props$block$image3 !== void 0 && _props$block$image3.title ? md.renderInline(props.block.image.title) : undefined;
    });
    const copyright = computed(() => {
      var _props$block$image4;

      return (_props$block$image4 = props.block.image) !== null && _props$block$image4 !== void 0 && _props$block$image4.copyright ? md.renderInline(props.block.image.copyright) : undefined;
    });
    const imageClasses = computed(() => {
      const result = ['block-image__image'];

      if (loaded.value) {
        result.push('block-image__image--loaded');
      } else {
        result.push('block-image__image--loading');
      }

      return result;
    });
    const overlays = computed(() => props.block.overlays || []);
    const linkComponent = computed(() => {
      var _props$block$link, _props$block$link2;

      return ((_props$block$link = props.block.link) !== null && _props$block$link !== void 0 && _props$block$link.url || (_props$block$link2 = props.block.link) !== null && _props$block$link2 !== void 0 && _props$block$link2.cached_url) && resolveLink(() => props.block.link).url ? 'router-link' : 'div';
    });
    const linkHref = computed(() => resolveLink(() => props.block.link).url);
    const linkTarget = computed(() => {
      if (props.block.linkTarget) {
        return props.block.linkTarget;
      }

      if (resolveLink(() => props.block.link).isExternal) {
        return '_blank';
      }

      return '_self';
    });
    watchEffect(() => {
      style.value = styleBuild(props.block);
    });
    watch(() => {
      var _props$block$image5;

      return (_props$block$image5 = props.block.image) === null || _props$block$image5 === void 0 ? void 0 : _props$block$image5.filename;
    }, () => {
      lowres.value = imageResize(props.block.image, {
        width: 10
      });
      displayedUrl.value = lowres.filename;
      loadImage();
    });
    onMounted(() => {
      loadImage();
    });

    function loadImage() {
      if (!isVideo.value) {
        nextTick(() => {
          var _hiresImage$value;

          hiresImage.value = imageResize(props.block.image, {
            width: imgElement.clientWidth
          });

          if (!((_hiresImage$value = hiresImage.value) !== null && _hiresImage$value !== void 0 && _hiresImage$value.filename)) {
            return;
          }

          const img = new Image();

          img.onload = () => {
            displayedUrl.value = hiresImage.value.filename;
            loaded.value = true;
          };

          img.src = hiresImage.value.filename;
        });
      }
    }

    const md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true
    });
    return (_ctx, _cache) => {
      const _component_blocks_repeater = resolveComponent("blocks-repeater");

      return openBlock(), createElementBlock(Fragment, null, [style.value ? (openBlock(), createBlock(resolveDynamicComponent('style'), {
        key: 0,
        type: "text/css"
      }, {
        default: withCtx(() => [createTextVNode(toDisplayString(style.value), 1)]),
        _: 1
      })) : createCommentVNode("", true), createElementVNode("figure", {
        class: normalizeClass(unref(containerClasses))
      }, [(openBlock(), createBlock(resolveDynamicComponent(unref(linkComponent)), {
        to: unref(linkHref),
        target: unref(linkTarget),
        class: "block-image__margin"
      }, {
        default: withCtx(() => {
          var _hiresImage$value2, _hiresImage$value3;

          return [createElementVNode("div", _hoisted_1, [_hoisted_2, !unref(isVideo) ? (openBlock(), createElementBlock("img", {
            key: 0,
            ref_key: "imgElement",
            ref: imgElement,
            class: normalizeClass(unref(imageClasses)),
            src: displayedUrl.value,
            width: (_hiresImage$value2 = hiresImage.value) === null || _hiresImage$value2 === void 0 ? void 0 : _hiresImage$value2.width,
            height: (_hiresImage$value3 = hiresImage.value) === null || _hiresImage$value3 === void 0 ? void 0 : _hiresImage$value3.height,
            alt: unref(alt)
          }, null, 10, _hoisted_3)) : createCommentVNode("", true), unref(isVideo) ? (openBlock(), createElementBlock("video", _hoisted_4, [createElementVNode("source", {
            src: __props.block.image.filename,
            type: "video/mp4"
          }, null, 8, _hoisted_5)])) : createCommentVNode("", true), (openBlock(true), createElementBlock(Fragment, null, renderList(unref(overlays), overlay => {
            return openBlock(), createElementBlock("div", {
              key: overlay._uid,
              class: normalizeClass(`block-image__overlay-container block-image__overlay-container--${overlay._uid}`)
            }, [createElementVNode("div", {
              class: normalizeClass(`block-image__overlay block-image__overlay--${overlay._uid}`)
            }, [createVNode(_component_blocks_repeater, {
              blocks: overlay.content
            }, null, 8, ["blocks"])], 2)], 2);
          }), 128))]), unref(caption) ? (openBlock(), createElementBlock("figcaption", {
            key: 0,
            class: "block-image__caption block-image__margin",
            innerHTML: unref(caption)
          }, null, 8, _hoisted_6)) : createCommentVNode("", true), unref(copyright) ? (openBlock(), createElementBlock("small", {
            key: 1,
            class: "block-image__copyright block-image__margin",
            innerHTML: unref(copyright)
          }, null, 8, _hoisted_7)) : createCommentVNode("", true)];
        }),
        _: 1
      }, 8, ["to", "target"]))], 2)], 64);
    };
  }

});

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = "\n.block-image {\r\n  margin: 0;\r\n  overflow: hidden;\n}\n.block-image__margin {\r\n  display: block;\n}\n.block-image__frame {\r\n  position: relative;\n}\n.block-image__image, .block-image__video {\r\n  display: block;\n}\n.block-image__image--loading {\r\n  filter: blur(5px);\n}\n.block-image__overlay-container {\r\n  position: absolute;\r\n  pointer-events: none;\r\n  top: 0;\r\n  bottom: 0;\r\n  left: 0;\r\n  right: 0;\n}\n.block-image__overlay {\r\n  position: absolute;\r\n  overflow: auto;\r\n  pointer-events: auto;\n}\r\n";
styleInject(css_248z);

// Import vue component

// Default export is installable instance of component.
// IIFE injects install function into component, allowing component
// to be registered via Vue.use() as well as Vue.component(),
var entry_esm = /*#__PURE__*/(() => {
  // Assign InstallableComponent type
  const installable = script; // Attach install function executed by Vue.use()

  installable.install = app => {
    app.component('StoryblokImage', installable);
  };

  return installable;
})(); // It's possible to expose named exports when writing components that can
// also be used as directives, etc. - eg. import { RollupDemoDirective } from 'rollup-demo';
// export const RollupDemoDirective = directive;

export { entry_esm as default };
